import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Play, Star, X } from "lucide-react";

const Testimonials = () => {
  // Sample testimonials with screenshots/videos
  const testimonials = [
    {
      type: "image",
      src: "https://ik.imagekit.io/tdlebsr5e/WhatsApp%20Image%202025-05-16%20at%2016.07.37%20(1).jpeg?updatedAt=17474868424330",
      alt: "Grand wedding celebration at our venue",
      rating: 5,
      caption: "A perfect wedding experience with elegant décor and seamless arrangements",
      clientName: "",
      clientLocation: "",
      testimonial: "Our wedding at The Gulmohar Grand was nothing short of magical. The team ensured every detail was perfect—from the beautiful venue setup to the delicious food. Guests were truly impressed!"
    },
    {
      type: "image",
      src: "https://ik.imagekit.io/tdlebsr5e/PHOTO-2025-05-16-16-40-12.jpg?updatedAt=1747486842229",
      alt: "Karan Aujla with our staff at The Gulmohar Grand",
      rating: 5,
      caption: "A star-studded moment as Karan Aujla visited our hotel",
      clientName: "",
      clientLocation: "",
      testimonial: "It was a pleasure staying at The Gulmohar Grand. The hospitality was top-notch, and the warmth of the staff truly made it feel like home."
    },
    {
      type: "video",
      thumbnail: "https://ik.imagekit.io/tdlebsr5e/PHOTO-2025-05-16-16-33-48.jpg?updatedAt=1747486842144",
      videoUrl: "https://ik.imagekit.io/tdlebsr5e/VIDEO-2025-05-16-17-14-47.mp4?updatedAt=1747486846098",
      rating: 5,
      caption: "",
      clientName: "",
      clientLocation: "",
      testimonial: ""
    },
    {
      type: "image",
      src: "https://ik.imagekit.io/tdlebsr5e/WhatsApp%20Image%202025-05-16%20at%2016.07.36.jpeg?updatedAt=1747486842140",
      alt: "Govinda at The Gulmohar Grand",
      rating: 5,
      caption: "Legendary Bollywood actor Govinda graced our property with his presence",
      clientName: "",
      clientLocation: "",
      testimonial: "Loved the ambience and hospitality at The Gulmohar Grand. A truly luxurious and peaceful escape!"
    },
    {
      type: "image",
      src: "https://ik.imagekit.io/tdlebsr5e/PHOTO-2025-05-16-16-33-48.jpg?updatedAt=17474868421440",
      alt: "",
      rating: 4,
      caption: "",
      clientName: "",
      clientLocation: "",
      testimonial: ""
    },
    {
      type: "video",
      thumbnail: "https://ik.imagekit.io/tdlebsr5e/PHOTO-2025-05-16-16-33-48.jpg?updatedAt=1747486842144",
      videoUrl: "https://ik.imagekit.io/tdlebsr5e/VIDEO-2025-05-16-17-14-48.mp4?updatedAt=1747486844770",
      rating: 5,
      caption: "",
      clientName: "",
      clientLocation: "",
      testimonial: ""
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeVideoIndex, setActiveVideoIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const timeoutRef = useRef(null);
  const videoRef = useRef(null);
  const maxVisible = 3; // Maximum number of visible items at once on desktop

  // Handle auto-scrolling
  useEffect(() => {
    if (activeVideoIndex !== null || isModalOpen) return; // Stop auto-scrolling if video is playing or modal is open

    const resetTimeout = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };

    resetTimeout();
    timeoutRef.current = setTimeout(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
    }, 5000);

    return () => {
      resetTimeout();
    };
  }, [currentIndex, testimonials.length, activeVideoIndex, isModalOpen]);

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  };

  // For responsive design, calculate visible items
  const getVisibleItems = () => {
    const items = [];
    for (let i = 0; i < maxVisible; i++) {
      const index = (currentIndex + i) % testimonials.length;
      items.push(testimonials[index]);
    }
    return items;
  };

  const visibleItems = getVisibleItems();

  // Function to generate star rating
  const renderStars = (rating) => {
    return Array(rating).fill(0).map((_, i) => (
      <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
    ));
  };

  // Function to handle video playback
  const playVideo = (index) => {
    setActiveVideoIndex(index);
    setIsModalOpen(true);
  };

  // Function to close the video modal
  const closeVideoModal = () => {
    setActiveVideoIndex(null);
    setIsModalOpen(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  return (
    <div className="py-20 bg-gradient-to-b from-amber-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16" data-aos="fade-up">
          <div className="flex items-center justify-center mb-6">
            <div className="w-12 h-px bg-amber-600 mr-4"></div>
            <Star className="text-amber-600 h-8 w-8" />
            <div className="w-12 h-px bg-amber-600 ml-4"></div>
          </div>
          <h3 className="text-xs tracking-widest uppercase text-amber-600 mb-3">
            Guest Experiences
          </h3>
          <h2 className="text-3xl md:text-4xl font-light text-gray-800 mb-6">
            CAPTURED MOMENTS
          </h2>
          <p className="max-w-2xl mx-auto text-gray-600">
            Real experiences from our cherished guests at The Gulmohar Grand
          </p>
        </div>

        {/* Mobile Carousel (Single Item View) */}
        <div className="md:hidden relative">
          <div className="overflow-hidden">
            <div className="relative bg-gray-100 rounded-lg shadow-xl overflow-hidden" style={{ height: "500px" }}>
              {testimonials[currentIndex].type === "image" ? (
                <img
                  src={testimonials[currentIndex].src}
                  alt={testimonials[currentIndex].alt}
                  className="w-full h-full object-cover"
                />
              ) : activeVideoIndex === currentIndex ? (
                <video
                  src={testimonials[currentIndex].videoUrl}
                  className="w-full h-full object-cover"
                  controls
                  autoPlay
                  playsInline
                ></video>
              ) : (
                <div className="relative w-full h-full">
                  <video
                    src={testimonials[currentIndex].videoUrl}
                    className="w-full h-full object-cover"
                    muted
                    loop
                    playsInline
                  ></video>
                  <button
                    onClick={() => playVideo(currentIndex)}
                    className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 hover:bg-opacity-30 transition-all duration-300"
                  >
                    <div className="w-16 h-16 rounded-full bg-white bg-opacity-80 flex items-center justify-center pl-1">
                      <Play className="w-8 h-8 text-amber-700" />
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Navigation Controls */}
          <div className="flex justify-between items-center mt-6">
            <button
              onClick={handlePrev}
              className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-amber-100 transition-colors duration-300"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5 text-amber-700" />
            </button>

            <div className="flex justify-center space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${currentIndex === index ? "bg-amber-600 w-8" : "bg-gray-300"
                    }`}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-amber-100 transition-colors duration-300"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5 text-amber-700" />
            </button>
          </div>
        </div>

        {/* Desktop View (Multiple Items) */}
        <div className="hidden md:block relative">
          <div className="relative overflow-hidden">
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-6">
              {visibleItems.map((item, idx) => (
                <div
                  key={idx}
                  className="relative bg-gray-100 rounded-lg shadow-xl overflow-hidden transform transition-transform duration-500 hover:scale-105"
                  style={{ height: "600px" }}
                  data-aos="fade-up"
                  data-aos-delay={100 * idx}
                >
                  {item.type === "image" ? (
                    <img
                      src={item.src}
                      alt={item.alt}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="relative w-full h-full">
                      <video
                        src={item.videoUrl}
                        className="w-full h-full object-cover"
                        muted
                        loop
                        playsInline
                      />
                      <button
                        onClick={() => playVideo((currentIndex + idx) % testimonials.length)}
                        className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 hover:bg-opacity-30 transition-all duration-300"
                      >
                        <div className="w-16 h-16 rounded-full bg-white bg-opacity-80 flex items-center justify-center pl-1">
                          <Play className="w-8 h-8 text-amber-700" />
                        </div>
                      </button>
                    </div>
                  )}

                  {/* Testimonial Overlay at Top */}
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/70 to-transparent p-4">
                    <div className="flex mb-1">
                      {renderStars(item.rating)}
                    </div>
                    <p className="text-sm font-medium text-white line-clamp-3">{item.testimonial}</p>
                    <div className="flex items-center mt-2">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-amber-300">{item.clientName}</p>
                        <p className="text-xs text-gray-300">{item.clientLocation}</p>
                      </div>
                    </div>
                  </div>

                  {/* Caption at Bottom */}
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-3 text-center">
                    <p className="text-sm font-light">{item.caption}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={handlePrev}
            className="absolute top-1/2 -left-4 transform -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-amber-100 transition-colors duration-300"
            aria-label="Previous"
          >
            <ChevronLeft className="w-6 h-6 text-amber-700" />
          </button>
          <button
            onClick={handleNext}
            className="absolute top-1/2 -right-4 transform -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-amber-100 transition-colors duration-300"
            aria-label="Next"
          >
            <ChevronRight className="w-6 h-6 text-amber-700" />
          </button>
        </div>

        {/* View All Gallery Button */}
        {/* <div className="text-center mt-12">
          <button className="group px-8 py-4 bg-amber-600 hover:bg-amber-700 text-white border border-amber-500 rounded-none transition-all duration-300 overflow-hidden relative">
            <span className="relative z-10 font-light tracking-wider uppercase flex items-center">
              View Complete Gallery
              <ChevronRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
            <div className="absolute inset-0 bg-amber-800 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
          </button>
        </div> */}
      </div>

      {/* Video Modal - Only for Desktop */}
      {isModalOpen && activeVideoIndex !== null && (
        <div className="hidden md:flex fixed inset-0 z-50 bg-black bg-opacity-90 items-center justify-center p-4">
          <div className="relative w-full max-w-4xl bg-black rounded-lg overflow-hidden">
            <button
              onClick={closeVideoModal}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
              aria-label="Close video"
            >
              <X className="w-6 h-6" />
            </button>

            <video
              ref={videoRef}
              src={testimonials[activeVideoIndex].videoUrl}
              className="w-full aspect-video object-contain"
              controls
              autoPlay
            ></video>

            <div className="bg-gray-900 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-300 font-medium text-lg">{testimonials[activeVideoIndex].clientName}</p>
                  <p className="text-gray-400 text-sm">{testimonials[activeVideoIndex].clientLocation}</p>
                </div>
                <div className="flex">
                  {renderStars(testimonials[activeVideoIndex].rating)}
                </div>
              </div>
              <p className="text-white mt-2">{testimonials[activeVideoIndex].testimonial}</p>
              <p className="text-gray-400 mt-4 text-sm">{testimonials[activeVideoIndex].caption}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Testimonials;